import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { LocalNotifications } from "@capacitor/local-notifications";
import { createContext, useContext, useEffect, useMemo, useRef, useState, type PropsWithChildren } from "react";
import { ApiError, apiRequest, clearNativeSessionToken, setNativeSessionToken } from "../lib/api";
import { type StaffOrder, type StaffOrderStatus } from "../data/staffData";

interface SessionUser {
  id: string;
  role: "staff" | "admin";
  name: string;
  email: string;
  phone: string;
}

interface SessionPayload {
  token: string;
  expiresAt: string;
}

interface StaffAuthResponse {
  user: SessionUser;
  session?: SessionPayload;
}

interface StaffOrdersContextValue {
  isLoading: boolean;
  isAuthenticated: boolean;
  authUser: SessionUser | null;
  orders: StaffOrder[];
  activeOrders: StaffOrder[];
  summary: {
    newCount: number;
    rushCount: number;
    kitchenCount: number;
    handoffCount: number;
    courierCount: number;
    completedCount: number;
  };
  login: (input: { login: string; password: string }) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  setStatus: (orderId: string, status: StaffOrderStatus) => Promise<void>;
  advanceOrder: (orderId: string) => Promise<void>;
  cancelOrder: (orderId: string) => Promise<void>;
  getOrderById: (orderId: string) => StaffOrder | undefined;
}

const StaffOrdersContext = createContext<StaffOrdersContextValue | null>(null);
const STAFF_ORDERS_POLL_INTERVAL_MS = 5_000;
const STAFF_ORDERS_NOTIFICATION_CHANNEL_ID = "staff-orders-live";
const STAFF_SEEN_NEW_ORDERS_STORAGE_KEY = "izumrud_staff_seen_new_orders";

function orderWeight(order: StaffOrder) {
  const priorityWeight = order.priority === "rush" ? 0 : order.priority === "scheduled" ? 1 : 2;
  return priorityWeight * 1000 + order.dueInMinutes;
}

function sortOrders(orders: StaffOrder[]) {
  return [...orders].sort((left, right) => orderWeight(left) - orderWeight(right));
}

function getNewOrderIds(orders: StaffOrder[]) {
  return new Set(orders.filter((order) => order.status === "new").map((order) => order.id));
}

function formatNotificationTotal(total: number) {
  return `${new Intl.NumberFormat("ru-RU").format(total)} ₽`;
}

function getNotificationId(orderId: string, index: number) {
  const numericPart = Number(orderId.replace(/[^\d]/g, ""));
  const baseId = Number.isFinite(numericPart) && numericPart > 0 ? numericPart : Date.now() % 1_000_000;
  return baseId * 10 + index;
}

function loadSeenNewOrderIds() {
  if (typeof window === "undefined") {
    return new Set<string>();
  }

  try {
    const raw = window.localStorage.getItem(STAFF_SEEN_NEW_ORDERS_STORAGE_KEY);

    if (!raw) {
      return new Set<string>();
    }

    const parsed = JSON.parse(raw);

    if (!Array.isArray(parsed)) {
      return new Set<string>();
    }

    return new Set(parsed.filter((value): value is string => typeof value === "string" && value.length > 0));
  } catch {
    return new Set<string>();
  }
}

function persistSeenNewOrderIds(orderIds: Set<string>) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(STAFF_SEEN_NEW_ORDERS_STORAGE_KEY, JSON.stringify([...orderIds]));
  } catch {
    // Ignore storage errors so notifications can still work in-memory.
  }
}

export function StaffOrdersProvider({ children }: PropsWithChildren) {
  const [isLoading, setIsLoading] = useState(true);
  const [authUser, setAuthUser] = useState<SessionUser | null>(null);
  const [orders, setOrders] = useState<StaffOrder[]>([]);
  const seenNewOrderIdsRef = useRef<Set<string>>(loadSeenNewOrderIds());
  const notificationsBootstrappedRef = useRef(false);
  const notificationPermissionGrantedRef = useRef(!Capacitor.isNativePlatform());
  const notificationPermissionRequestedRef = useRef(false);
  const notificationChannelReadyRef = useRef(false);

  const ensureNotificationChannel = async () => {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== "android") {
      return;
    }

    if (notificationChannelReadyRef.current) {
      return;
    }

    try {
      await LocalNotifications.createChannel({
        id: STAFF_ORDERS_NOTIFICATION_CHANNEL_ID,
        name: "Новые заказы",
        description: "Оповещения о новых оплаченных заказах для смены",
        importance: 5,
        visibility: 1,
        vibration: true,
        lights: true
      });
      notificationChannelReadyRef.current = true;
    } catch (error) {
      console.error("Failed to prepare staff notification channel", error);
    }
  };

  const ensureNotificationPermission = async () => {
    if (!Capacitor.isNativePlatform()) {
      return false;
    }

    if (notificationPermissionGrantedRef.current) {
      return true;
    }

    try {
      const permission = await LocalNotifications.checkPermissions();

      if (permission.display === "granted") {
        notificationPermissionGrantedRef.current = true;
        await ensureNotificationChannel();
        return true;
      }

      if (notificationPermissionRequestedRef.current) {
        return false;
      }

      notificationPermissionRequestedRef.current = true;
      const requestedPermission = await LocalNotifications.requestPermissions();
      notificationPermissionGrantedRef.current = requestedPermission.display === "granted";
      if (notificationPermissionGrantedRef.current) {
        await ensureNotificationChannel();
      }
      return notificationPermissionGrantedRef.current;
    } catch (error) {
      console.error("Failed to prepare local notifications", error);
      return false;
    }
  };

  const notifyAboutNewOrders = async (nextOrders: StaffOrder[], options?: { includeCurrentUnseen?: boolean }) => {
    const shouldNotify = notificationsBootstrappedRef.current || options?.includeCurrentUnseen;
    const nextNewOrderIds = getNewOrderIds(nextOrders);
    const freshOrders = shouldNotify
      ? nextOrders.filter((order) => order.status === "new" && !seenNewOrderIdsRef.current.has(order.id))
      : [];

    seenNewOrderIdsRef.current = nextNewOrderIds;
    persistSeenNewOrderIds(nextNewOrderIds);
    notificationsBootstrappedRef.current = true;

    if (!freshOrders.length) {
      return;
    }

    const notificationsEnabled = await ensureNotificationPermission();

    if (!notificationsEnabled) {
      return;
    }

    await LocalNotifications.schedule({
      notifications: freshOrders.map((order, index) => ({
        id: getNotificationId(order.id, index),
        title: "Новый оплаченный заказ",
        body: `${order.id} • ${order.customerName} • ${formatNotificationTotal(order.total)}`,
        largeBody: `${order.deliveryMode === "pickup" ? "Самовывоз" : "Доставка"} • ${order.address}`,
        summaryText: order.deliveryMode === "pickup" ? "Самовывоз" : "Доставка",
        channelId: Capacitor.getPlatform() === "android" ? STAFF_ORDERS_NOTIFICATION_CHANNEL_ID : undefined,
        sound: Capacitor.getPlatform() === "android" ? undefined : "default",
        schedule: {
          at: new Date(Date.now() + index * 500),
          allowWhileIdle: true
        },
        autoCancel: true,
        extra: {
          orderId: order.id
        }
      }))
    });
  };

  const applyOrders = async (nextOrders: StaffOrder[], options?: { announceNew?: boolean; includeCurrentUnseen?: boolean }) => {
    const sortedOrders = sortOrders(nextOrders);
    setOrders(sortedOrders);

    if (options?.announceNew) {
      await notifyAboutNewOrders(sortedOrders, { includeCurrentUnseen: options.includeCurrentUnseen });
      return;
    }

    const nextNewOrderIds = getNewOrderIds(sortedOrders);
    seenNewOrderIdsRef.current = nextNewOrderIds;
    persistSeenNewOrderIds(nextNewOrderIds);
    notificationsBootstrappedRef.current = true;
  };

  const resetGuest = () => {
    setAuthUser(null);
    setOrders([]);
    seenNewOrderIdsRef.current = loadSeenNewOrderIds();
    notificationsBootstrappedRef.current = false;
  };

  const persistNativeSession = (session?: SessionPayload) => {
    if (session?.token) {
      setNativeSessionToken("staff", session.token);
      return;
    }

    clearNativeSessionToken("staff");
  };

  const refreshOrders = async (options?: { announceNew?: boolean }) => {
    if (!authUser) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await apiRequest<{ orders: StaffOrder[] }>("/api/staff/orders");
      await applyOrders(response.orders, options);
    } catch (error) {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        clearNativeSessionToken("staff");
        resetGuest();
        return;
      }

      throw error;
    }
  };

  useEffect(() => {
    const bootstrap = async () => {
      setIsLoading(true);

      try {
        const me = await apiRequest<{ user: SessionUser }>("/api/staff/me");
        setAuthUser(me.user);
        const ordersResponse = await apiRequest<{ orders: StaffOrder[] }>("/api/staff/orders");
        await applyOrders(ordersResponse.orders, {
          announceNew: true,
          includeCurrentUnseen: true
        });
      } catch (error) {
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
          clearNativeSessionToken("staff");
          resetGuest();
        } else {
          console.error(error);
        }
      } finally {
        setIsLoading(false);
      }
    };

    void bootstrap();
  }, []);

  useEffect(() => {
    if (!authUser || !Capacitor.isNativePlatform()) {
      return;
    }

    void ensureNotificationPermission();
  }, [authUser]);

  useEffect(() => {
    if (!authUser || !Capacitor.isNativePlatform()) {
      return;
    }

    const handle = CapacitorApp.addListener("resume", () => {
      void refreshOrders({ announceNew: true });
    });

    return () => {
      void handle.then((listener) => listener.remove());
    };
  }, [authUser]);

  useEffect(() => {
    if (!authUser) {
      return;
    }

    let isCancelled = false;

    const pollOrders = async () => {
      try {
        const response = await apiRequest<{ orders: StaffOrder[] }>("/api/staff/orders");

        if (isCancelled) {
          return;
        }

        await applyOrders(response.orders, { announceNew: true });
      } catch (error) {
        if (isCancelled) {
          return;
        }

        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
          clearNativeSessionToken("staff");
          resetGuest();
          return;
        }

        console.error("Staff orders polling failed", error);
      }
    };

    const intervalId = window.setInterval(() => {
      void pollOrders();
    }, STAFF_ORDERS_POLL_INTERVAL_MS);

    return () => {
      isCancelled = true;
      window.clearInterval(intervalId);
    };
  }, [authUser]);

  const value = useMemo<StaffOrdersContextValue>(() => {
    const sortedOrders = sortOrders(orders);
    const activeOrders = sortedOrders.filter(
      (order) => order.status !== "completed" && order.status !== "cancelled"
    );
    const summary = {
      newCount: sortedOrders.filter((order) => order.status === "new").length,
      rushCount: activeOrders.filter((order) => order.priority === "rush").length,
      kitchenCount: activeOrders.filter((order) => ["accepted", "cooking"].includes(order.status)).length,
      handoffCount: activeOrders.filter((order) => order.status === "packing").length,
      courierCount: activeOrders.filter((order) => order.status === "courier").length,
      completedCount: sortedOrders.filter((order) => order.status === "completed").length
    };

    return {
      isLoading,
      isAuthenticated: Boolean(authUser),
      authUser,
      orders: sortedOrders,
      activeOrders,
      summary,
      login: async (input) => {
        setIsLoading(true);

        try {
          const response = await apiRequest<StaffAuthResponse>("/api/staff/auth/login", {
            method: "POST",
            body: JSON.stringify(input)
          });
          persistNativeSession(response.session);
          setAuthUser(response.user);
          const ordersResponse = await apiRequest<{ orders: StaffOrder[] }>("/api/staff/orders");
          await applyOrders(ordersResponse.orders, {
            announceNew: true,
            includeCurrentUnseen: true
          });
        } finally {
          setIsLoading(false);
        }
      },
      logout: async () => {
        try {
          await apiRequest("/api/staff/auth/logout", {
            method: "POST"
          });
        } finally {
          clearNativeSessionToken("staff");
          resetGuest();
        }
      },
      refresh: async () => {
        setIsLoading(true);

        try {
          await refreshOrders();
        } finally {
          setIsLoading(false);
        }
      },
      setStatus: async (orderId, status) => {
        const response = await apiRequest<{ orders: StaffOrder[] }>(`/api/staff/orders/${orderId}/status`, {
          method: "PATCH",
          body: JSON.stringify({ status })
        });
        await applyOrders(response.orders);
      },
      advanceOrder: async (orderId) => {
        const response = await apiRequest<{ orders: StaffOrder[] }>(`/api/staff/orders/${orderId}/advance`, {
          method: "POST"
        });
        await applyOrders(response.orders);
      },
      cancelOrder: async (orderId) => {
        const response = await apiRequest<{ orders: StaffOrder[] }>(`/api/staff/orders/${orderId}/cancel`, {
          method: "POST"
        });
        await applyOrders(response.orders);
      },
      getOrderById: (orderId) => sortedOrders.find((order) => order.id === orderId)
    };
  }, [authUser, isLoading, orders]);

  return <StaffOrdersContext.Provider value={value}>{children}</StaffOrdersContext.Provider>;
}

export function useStaffOrders() {
  const context = useContext(StaffOrdersContext);

  if (!context) {
    throw new Error("useStaffOrders must be used within StaffOrdersProvider");
  }

  return context;
}
